<template>
    <div id="email_analytics_grid">
      <InfoGrid :columns="3">
        <ContainerInfoItem
          v-for="(data, key) in analyticsData"
          :key="key"
          source="marketing_email_activity_analytics"
          :title="data.title"
          :description="range?.label || ''"
          :value="formatAnalyticsValue(data.value, data.unit)"
          :is-loading="data.isLoading"
        />
      </InfoGrid>
     </div>
  </template>
  
  <script setup>
  import { computed, reactive, watch } from "vue";
  import { formatAnalyticsValue } from "@/src/utils/analyticUtils.js";
  import {
    marketingEmailActions,
    units,
  } from "@/src/constants/analyticConstants.js";
  import store from "@/src/store/index.js";
  import AnalyticService from "@/src/api/AnalyticService.js";
  import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
  import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
  import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";
  import dayjs from "dayjs";
  
  const range = computed(() => store.getters["analytics/getRange"]);
  const userIds = computed(() => store.getters["analytics/getUserIds"]);
  const teamIds = computed(() => store.getters["analytics/getTeamIds"]);
  
  const analyticsData = reactive([
    {
      id: marketingEmailActions.EMAIL_SENT,
      title: "Emails sent",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingEmailActions.TOTAL_DELIVERED,
      title: "Emails delivered",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingEmailActions.EMAIL_OPENED,
      title: "Email opens",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingEmailActions.EMAIL_CLICKED,
      title: "Email clicks",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingEmailActions.EMAIL_BOUNCED,
      title: "Bounced",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingEmailActions.TOTAL_UNSUBSCRIBED,
      title: "Unsubscribed",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingEmailActions.DELIVERY_RATE,
      title: "Delivery rate",
      value: 0,
      isLoading: false,
      unit: units.RATE,
    },
    {
      id: marketingEmailActions.OPEN_RATE,
      title: "Open rate",
      value: 0,
      isLoading: false,
      unit: units.RATE,
    },
    {
      id: marketingEmailActions.CLICK_RATE,
      title: "Click rate",
      value: 0,
      isLoading: false,
      unit: units.RATE,
    },
  ]);

  const fetchAnalytics = async () => {
    if (!range.value) {
      return;
    }

    let start, end;

    if (range.value.start && range.value.end) {
      start = range.value.start;
      end = range.value.end;
    } else {
      return;
    }

    analyticsData.forEach((item) => {
      item.isLoading = true;
    });
  
    try {
      const params = {
        start_date: start,
        end_date: end,
        channel_type: "EMAIL",
      };
      
      const response = await AnalyticService.V2.fetchWorkspaceMarketingCampaignStats(params);
      
      if (response?.success || response?.data || response) {
        const data = response?.data || response;
        
        const mapping = {
          [marketingEmailActions.EMAIL_SENT]: "total_sent",
          [marketingEmailActions.EMAIL_OPENED]: "total_opened",
          [marketingEmailActions.EMAIL_CLICKED]: "total_clicked",
          [marketingEmailActions.EMAIL_BOUNCED]: "total_bounced",
          [marketingEmailActions.TOTAL_DELIVERED]: "total_delivered",
          [marketingEmailActions.TOTAL_UNSUBSCRIBED]: "total_unsubscribed",
          [marketingEmailActions.DELIVERY_RATE]: "delivery_rate",
          [marketingEmailActions.OPEN_RATE]: "open_rate",
          [marketingEmailActions.CLICK_RATE]: "click_rate",
        };
        
        analyticsData.forEach((item) => {
          const apiKey = mapping[item.id] || item.id;
          let value = 0;
          
          if (data[apiKey] !== undefined && data[apiKey] !== null) {
            value = data[apiKey];
          }
          
          if (item.unit === units.RATE && value !== null && value !== undefined) {
            value = value * 100;
          }
          
          item.value = value;
        });
      }
    } catch (error) {
      console.error("An error occurred while fetching analytics: ", error);
    } finally {
      analyticsData.forEach((item) => {
        item.isLoading = false;
      });
    }
  };
  
  watch(
    [range],
    () => {
      if (range.value?.start && range.value?.end) {
        fetchAnalytics();
      }
    },
    { deep: true, immediate: true }
  );
  
  useAnalyticsRefresh(fetchAnalytics, [range, userIds, teamIds]);
  </script>
  
  